import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { MapContainer, TileLayer, CircleMarker, Tooltip as LTooltip, ZoomControl } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import api from "../lib/api";
import { getDayCache, setDayCache } from "../lib/dayCache";
import { Input, MockBadge, Spinner } from "../components/ui";
import { Search, ExternalLink } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import {
  buildMarketOptions,
  marketOptionMatchesSelection,
  normalizeMarketSearch,
} from "../lib/marketMeta";
import {
  FilterCell,
  MarketMultiSelect,
  Metric,
  RangeCell,
  percentile99,
} from "../components/filters";

// Fachliche Sanity-Grenzen für Mietangebote (monatlich, €) — dieselbe Rolle
// wie SIZE_SANITY/PRICE_SANITY auf der Kauf-Seite: Datenmüll darf die
// Slider-Endpunkte nicht verziehen.
const RENT_SIZE_SANITY = { min: 10, max: 400 };      // m²
const RENT_PRICE_SANITY = { min: 50, max: 20_000 };  // €/Monat

const UNDER_MEDIAN_OPTIONS = [0, 5, 10, 15];
const SEGMENT_OPTIONS = ["all", "frei", "gefoerdert"];
// Feature flags on rental docs (sorted like the backend contract). Legacy docs
// may lack the field entirely — that fails any selected flag.
const FLAG_OPTIONS = [
  "balkon", "dachgeschoss", "direktvergabe", "erdgeschoss",
  "loggia", "moebliert", "parkplatz", "provisionsfrei",
];

// Deterministic jitter (±0.01° ≈ ~1 km) so markers in the same PLZ don't pile
// up. Local copy — intentionally not shared with MarketMap.
function hashJitter(id) {
  let h = 0;
  for (let i = 0; i < String(id).length; i++) h = (Math.imul(31, h) + String(id).charCodeAt(i)) | 0;
  const u = h >>> 0;
  return [((u & 0xff) / 255 - 0.5) * 0.02, (((u >> 8) & 0xff) / 255 - 0.5) * 0.02];
}

// Same thresholds as the card badge: ≥70 green, 40–69 yellow, below/unknown grey.
function scoreColor(score) {
  if (score == null) return "#71717A";
  if (score >= 70) return "#00FF66";
  if (score >= 40) return "#FFCC00";
  return "#71717A";
}

function getFilterDefaults() {
  try {
    const raw = sessionStorage.getItem("immo_filters_mieten_v1");
    if (raw) return JSON.parse(raw);
  } catch {}
  return null;
}

export default function Mieten() {
  const { t } = useI18n();
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);

  const saved = getFilterDefaults();
  const [query, setQuery] = useState(saved?.query ?? "");
  const [marketFilter, setMarketFilter] = useState(saved?.marketFilter ?? []);
  const [marketQuery, setMarketQuery] = useState(saved?.marketQuery ?? "");
  const [sizeMin, setSizeMin] = useState(saved?.sizeMin ?? null);
  const [sizeMax, setSizeMax] = useState(saved?.sizeMax ?? null);
  const [priceMin, setPriceMin] = useState(saved?.priceMin ?? null);
  const [priceMax, setPriceMax] = useState(saved?.priceMax ?? null);
  const [minRooms, setMinRooms] = useState(saved?.minRooms ?? "");
  const [underMedian, setUnderMedian] = useState(saved?.underMedian ?? 0);
  const [onlyNew, setOnlyNew] = useState(saved?.onlyNew ?? false);
  const [onlyMrg, setOnlyMrg] = useState(saved?.onlyMrg ?? false);
  const [onlyUnbefristet, setOnlyUnbefristet] = useState(saved?.onlyUnbefristet ?? false);
  const [segment, setSegment] = useState(saved?.segment ?? "all");
  const [flags, setFlags] = useState(saved?.flags ?? []);
  const [view, setView] = useState(saved?.view ?? "list");
  const [sort, setSort] = useState(saved?.sort ?? "score");

  useEffect(() => {
    const CACHE_KEY = "immo_rentals_v2";
    (async () => {
      const cached = await getDayCache(CACHE_KEY);
      if (cached) {
        setAll(cached);
        setLoading(false);
        return;
      }
      try {
        const { data } = await api.get("/rentals?limit=20000");
        setAll(data.rentals);
        setDayCache(CACHE_KEY, data.rentals);
      } catch (e) {
        console.error("Rentals load failed", e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Persist filter state across navigation
  useEffect(() => {
    try {
      sessionStorage.setItem("immo_filters_mieten_v1", JSON.stringify({
        query, marketFilter, marketQuery, sizeMin, sizeMax, priceMin, priceMax,
        minRooms, underMedian, onlyNew, onlyMrg, onlyUnbefristet, segment, flags, view, sort,
      }));
    } catch {}
  }, [query, marketFilter, marketQuery, sizeMin, sizeMax, priceMin, priceMax, minRooms, underMedian, onlyNew, onlyMrg, onlyUnbefristet, segment, flags, view, sort]);

  // Market options across all countries — the rental corpus is AT+DE like the
  // listings corpus, but there is no separate country filter here (P0).
  const marketOptions = useMemo(() => buildMarketOptions(all, "ALL"), [all]);

  const filteredMarketOptions = useMemo(() => {
    const q = normalizeMarketSearch(marketQuery);
    if (!q) return marketOptions;
    return marketOptions.filter((opt) => opt.searchText.includes(q));
  }, [marketOptions, marketQuery]);

  // Slider bounds: sanitized values + 99th percentile as the upper end, same
  // reasoning as on Listings (junk values must not break the sliders).
  const sizeBounds = useMemo(() => {
    const values = all
      .map((r) => Number(r.size_m2))
      .filter((n) => Number.isFinite(n) && n >= RENT_SIZE_SANITY.min && n <= RENT_SIZE_SANITY.max)
      .sort((a, b) => a - b);
    return {
      min: values.length ? Math.floor(values[0]) : 0,
      max: values.length ? Math.ceil(percentile99(values)) : 150,
    };
  }, [all]);

  const priceBounds = useMemo(() => {
    const values = all
      .map((r) => Number(r.price))
      .filter((n) => Number.isFinite(n) && n >= RENT_PRICE_SANITY.min && n <= RENT_PRICE_SANITY.max)
      .sort((a, b) => a - b);
    return {
      min: values.length ? Math.floor(values[0]) : 0,
      max: values.length ? Math.ceil(percentile99(values)) : 5000,
    };
  }, [all]);

  useEffect(() => {
    if (!all.length) return;
    setSizeMin((prev) => (prev == null ? sizeBounds.min : prev));
    setSizeMax((prev) => (prev == null ? sizeBounds.max : prev));
    setPriceMin((prev) => (prev == null ? priceBounds.min : prev));
    setPriceMax((prev) => (prev == null ? priceBounds.max : prev));
  }, [all.length, sizeBounds.min, sizeBounds.max, priceBounds.min, priceBounds.max]);

  const sizeMinValue = sizeMin != null ? Math.max(sizeBounds.min, Number(sizeMin)) : sizeBounds.min;
  const sizeMaxValue = sizeMax != null ? Math.min(sizeBounds.max, Number(sizeMax)) : sizeBounds.max;
  const priceMinValue = priceMin != null ? Math.max(priceBounds.min, Number(priceMin)) : priceBounds.min;
  const priceMaxValue = priceMax != null ? Math.min(priceBounds.max, Number(priceMax)) : priceBounds.max;
  const effectiveSizeMin = Math.min(sizeMinValue, sizeMaxValue);
  const effectiveSizeMax = Math.max(sizeMinValue, sizeMaxValue);
  const effectivePriceMin = Math.min(priceMinValue, priceMaxValue);
  const effectivePriceMax = Math.max(priceMinValue, priceMaxValue);

  const rows = useMemo(() => {
    let r = [...all];
    if (marketFilter.length > 0) r = r.filter((x) => marketOptionMatchesSelection(x, marketFilter));
    if (effectiveSizeMin > sizeBounds.min || effectiveSizeMax < sizeBounds.max) {
      r = r.filter((x) => {
        const size = x.size_m2 ?? Infinity;
        return size >= effectiveSizeMin && size <= effectiveSizeMax;
      });
    }
    if (effectivePriceMin > priceBounds.min || effectivePriceMax < priceBounds.max) {
      r = r.filter((x) => {
        const price = x.price ?? Infinity;
        return price >= effectivePriceMin && price <= effectivePriceMax;
      });
    }
    if (minRooms !== "" && minRooms != null) {
      // rooms null fails the gate — an unknown room count is not "≥ N rooms"
      r = r.filter((x) => x.rooms != null && x.rooms >= Number(minRooms));
    }
    if (underMedian > 0) {
      // delta_pct null is excluded: no benchmark, no "under median" claim
      r = r.filter((x) => x.delta_pct != null && x.delta_pct <= -underMedian);
    }
    if (onlyNew) r = r.filter((x) => x.is_new === true);
    if (onlyMrg) r = r.filter((x) => x.mrg_applies === true);
    // null/unknown excluded — an unverified lease term is not "unbefristet"
    if (onlyUnbefristet) r = r.filter((x) => x.befristet === false);
    if (segment === "gefoerdert") r = r.filter((x) => x.segment === "gefoerdert");
    // "frei" keeps everything not explicitly tagged — null means free market
    if (segment === "frei") r = r.filter((x) => x.segment !== "gefoerdert");
    // AND semantics: doc must carry ALL selected flags; missing/empty flags
    // array fails any selection (mirrors how unbefristet treats null)
    if (flags.length > 0) {
      r = r.filter((x) => flags.every((f) => Array.isArray(x.flags) && x.flags.includes(f)));
    }
    if (query.trim()) {
      const q = query.toLowerCase();
      r = r.filter((x) =>
        (x.title || "").toLowerCase().includes(q) ||
        (x.location || "").toLowerCase().includes(q)
      );
    }
    const sortMap = {
      score: (a, b) => (b.deal_score ?? -Infinity) - (a.deal_score ?? -Infinity),
      delta: (a, b) => (a.delta_pct ?? Infinity) - (b.delta_pct ?? Infinity),
      fresh: (a, b) => new Date(b.first_seen || 0) - new Date(a.first_seen || 0),
      eur_sqm: (a, b) => (a.price_per_sqm ?? Infinity) - (b.price_per_sqm ?? Infinity),
      price: (a, b) => (a.price ?? Infinity) - (b.price ?? Infinity),
      dom: (a, b) => (b.days_on_market ?? -Infinity) - (a.days_on_market ?? -Infinity),
      savings: (a, b) => (b.mrg_savings_mo ?? -Infinity) - (a.mrg_savings_mo ?? -Infinity),
    };
    r.sort(sortMap[sort] || sortMap.score);
    return r;
  }, [all, query, marketFilter, effectiveSizeMin, effectiveSizeMax, effectivePriceMin, effectivePriceMax, sizeBounds.min, sizeBounds.max, priceBounds.min, priceBounds.max, minRooms, underMedian, onlyNew, onlyMrg, onlyUnbefristet, segment, flags, sort]);

  // ?focus=<unit_key> — scroll the matching card into view and highlight it.
  // Map view is switched back to list first; runs once per mount.
  const [searchParams] = useSearchParams();
  const focusKey = searchParams.get("focus");
  const [focusHighlight, setFocusHighlight] = useState(null);
  const focusDone = useRef(false);
  useEffect(() => {
    if (focusDone.current || !focusKey || loading) return;
    if (view === "map") {
      setView("list");
      return;
    }
    const el = document.querySelector(`[data-testid="rental-card-${focusKey}"]`);
    if (!el) return;
    focusDone.current = true;
    if (typeof el.scrollIntoView === "function") el.scrollIntoView({ block: "center" });
    setFocusHighlight(focusKey);
    const timer = setTimeout(() => setFocusHighlight(null), 3000);
    return () => clearTimeout(timer);
  }, [focusKey, loading, view, rows]);

  const mapPoints = useMemo(
    () => rows.filter((r) => r.lat != null && r.lon != null),
    [rows]
  );
  const mapBounds = useMemo(() => {
    if (!mapPoints.length) return null;
    return mapPoints.map((r) => {
      const [jLat, jLon] = hashJitter(r.unit_key);
      return [r.lat + jLat, r.lon + jLon];
    });
  }, [mapPoints]);

  if (loading) return <div className="p-10"><Spinner /></div>;

  return (
    <div className="reveal">
      {/* header */}
      <div className="px-6 md:px-10 pt-8 pb-5 border-b border-border bg-grid">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="overline">{t("mieten.eyebrow")}</div>
            <h1 className="mt-2 font-heading text-4xl font-black tracking-tight">{t("mieten.title")}</h1>
            <p className="mt-2 text-xs text-text-muted">
              {t("mieten.countLabel", { shown: rows.length, total: all.length, markets: marketOptions.length })}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex border border-border" data-testid="view-toggle">
              {["list", "map"].map((v) => {
                const active = view === v;
                return (
                  <button
                    key={v}
                    onClick={() => setView(v)}
                    data-testid={`view-toggle-${v}`}
                    className={`text-[10px] font-mono uppercase tracking-widest px-3 py-1.5 transition-colors ${
                      active ? "bg-signal-green/10 text-signal-green" : "text-text-muted hover:text-white"
                    }`}
                  >
                    {t(`mieten.view.${v}`)}
                  </button>
                );
              })}
            </div>
            <MockBadge />
          </div>
        </div>
      </div>

      {/* filter row 1 */}
      <div className="grid grid-cols-12 border-b border-border">
        <div className="col-span-12 md:col-span-2 p-4 border-r border-border flex items-center gap-2">
          <Search size={14} className="text-text-muted" />
          <Input
            data-testid="mieten-search"
            placeholder={t("common.search")}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="!border-none !px-0"
          />
        </div>
        <FilterCell label={t("mieten.market")}>
          <MarketMultiSelect
            options={filteredMarketOptions}
            value={marketFilter}
            onChange={setMarketFilter}
            t={t}
            query={marketQuery}
            onQueryChange={setMarketQuery}
          />
        </FilterCell>
        <FilterCell label={t("mieten.minRooms")}>
          <select data-testid="filter-min-rooms" value={minRooms} onChange={(e) => setMinRooms(e.target.value)} className="bg-bg border border-border text-xs font-mono px-2 py-1.5 text-white">
            <option value="">{t("common.all")}</option>
            <option value="1">1+</option>
            <option value="2">2+</option>
            <option value="3">3+</option>
            <option value="4">4+</option>
          </select>
        </FilterCell>
        <FilterCell label={t("mieten.sortBy")} last>
          <select data-testid="filter-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="bg-bg border border-border text-xs font-mono px-2 py-1.5 text-white">
            <option value="score">{t("mieten.sort.score")}</option>
            <option value="delta">{t("mieten.sort.delta")}</option>
            <option value="fresh">{t("mieten.sort.fresh")}</option>
            <option value="eur_sqm">{t("mieten.sort.eurSqm")}</option>
            <option value="price">{t("mieten.sort.price")}</option>
            <option value="dom">{t("mieten.sort.dom")}</option>
            <option value="savings">{t("mieten.sort.savings")}</option>
          </select>
        </FilterCell>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 border-b border-border">
        <RangeCell
          label={t("mieten.filters.sizeRange")}
          minValue={sizeMinValue}
          maxValue={sizeMaxValue}
          min={sizeBounds.min}
          max={sizeBounds.max}
          step={1}
          testidMin="filter-size-min"
          testidMax="filter-size-max"
          onMinChange={setSizeMin}
          onMaxChange={setSizeMax}
          minLabel={`${sizeMinValue} m²`}
          maxLabel={`${sizeMaxValue} m²`}
        />
        <RangeCell
          label={t("mieten.filters.priceRange")}
          minValue={priceMinValue}
          maxValue={priceMaxValue}
          min={priceBounds.min}
          max={priceBounds.max}
          step={25}
          testidMin="filter-price-min"
          testidMax="filter-price-max"
          onMinChange={setPriceMin}
          onMaxChange={setPriceMax}
          minLabel={`€${Math.round(priceMinValue).toLocaleString()}`}
          maxLabel={`€${Math.round(priceMaxValue).toLocaleString()}`}
          last
        />
      </div>

      {/* filter row 2 — delta + freshness chips */}
      <div className="border-b border-border px-4 py-3 flex items-start gap-3 flex-wrap">
        <span className="overline text-signal-green pt-1 shrink-0 w-24">{t("mieten.filters.underMedian")}</span>
        <div className="flex flex-wrap gap-1.5" data-testid="filter-under-median">
          {UNDER_MEDIAN_OPTIONS.map((pct) => {
            const active = underMedian === pct;
            return (
              <button
                key={pct}
                onClick={() => setUnderMedian(pct)}
                data-testid={`under-median-${pct}`}
                className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 border transition-colors ${
                  active ? "border-signal-green text-signal-green bg-signal-green/10" : "border-border text-text-muted hover:border-signal-green/50 hover:text-signal-green/70"
                }`}
              >
                {pct === 0 ? t("mieten.filters.off") : `≥ ${pct}%`}
              </button>
            );
          })}
          <button
            onClick={() => setOnlyNew((v) => !v)}
            data-testid="filter-only-new"
            className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 border transition-colors ${
              onlyNew ? "border-signal-green text-signal-green bg-signal-green/10" : "border-border text-text-muted hover:border-signal-green/50 hover:text-signal-green/70"
            }`}
          >
            {t("mieten.badges.new")}
          </button>
          <button
            onClick={() => setOnlyMrg((v) => !v)}
            data-testid="filter-only-mrg"
            className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 border transition-colors ${
              onlyMrg ? "border-signal-green text-signal-green bg-signal-green/10" : "border-border text-text-muted hover:border-signal-green/50 hover:text-signal-green/70"
            }`}
          >
            {t("mieten.filters.onlyMrg")}
          </button>
          <button
            onClick={() => setOnlyUnbefristet((v) => !v)}
            data-testid="filter-only-unbefristet"
            className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 border transition-colors ${
              onlyUnbefristet ? "border-signal-green text-signal-green bg-signal-green/10" : "border-border text-text-muted hover:border-signal-green/50 hover:text-signal-green/70"
            }`}
          >
            {t("mieten.filters.onlyUnbefristet")}
          </button>
        </div>
      </div>

      {/* filter row 3 — segment chips */}
      <div className="border-b border-border px-4 py-3 flex items-start gap-3 flex-wrap">
        <span className="overline text-signal-yellow pt-1 shrink-0 w-24">{t("mieten.filters.segment")}</span>
        <div className="flex flex-wrap gap-1.5" data-testid="filter-segment">
          {SEGMENT_OPTIONS.map((opt) => {
            const active = segment === opt;
            return (
              <button
                key={opt}
                onClick={() => setSegment(opt)}
                data-testid={`filter-segment-${opt}`}
                className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 border transition-colors ${
                  active ? "border-signal-yellow text-signal-yellow bg-signal-yellow/10" : "border-border text-text-muted hover:border-signal-yellow/50 hover:text-signal-yellow/70"
                }`}
              >
                {t(`mieten.filters.segment_${opt}`)}
              </button>
            );
          })}
        </div>
      </div>

      {/* filter row 4 — feature flag chips */}
      <div className="border-b border-border px-4 py-3 flex items-start gap-3 flex-wrap">
        <span className="overline text-signal-green pt-1 shrink-0 w-24">{t("mieten.filters.flags")}</span>
        <div className="flex flex-wrap gap-1.5" data-testid="filter-flags">
          {FLAG_OPTIONS.map((key) => {
            const active = flags.includes(key);
            return (
              <button
                key={key}
                onClick={() => setFlags((prev) => (prev.includes(key) ? prev.filter((f) => f !== key) : [...prev, key]))}
                data-testid={`filter-flag-${key}`}
                className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 border transition-colors ${
                  active ? "border-signal-green text-signal-green bg-signal-green/10" : "border-border text-text-muted hover:border-signal-green/50 hover:text-signal-green/70"
                }`}
              >
                {t(`mieten.filters.flag_${key}`)}
              </button>
            );
          })}
        </div>
      </div>

      {/* list / map */}
      {view === "map" ? (
        <div className="relative border-b border-border" style={{ height: "70vh" }} data-testid="mieten-map">
          <MapContainer
            {...(mapBounds ? { bounds: mapBounds } : { center: [47.7, 13.3], zoom: 7 })}
            style={{ height: "100%", width: "100%", background: "#09090B" }}
            scrollWheelZoom={true}
            zoomControl={false}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
            />
            <ZoomControl position="topright" />
            {mapPoints.map((r) => {
              const [jLat, jLon] = hashJitter(r.unit_key);
              const color = scoreColor(r.deal_score);
              const deltaValue = r.delta_pct != null ? `${r.delta_pct > 0 ? "+" : ""}${r.delta_pct}%` : "—";
              return (
                <CircleMarker
                  key={r.unit_key}
                  center={[r.lat + jLat, r.lon + jLon]}
                  radius={6}
                  pathOptions={{ color, fillColor: color, fillOpacity: 0.5, weight: 2 }}
                >
                  <LTooltip direction="top" offset={[0, -8]} opacity={1} className="!bg-bg !text-white !border-border">
                    <div className="font-mono text-[11px]">
                      <div className="font-bold text-white max-w-[220px] truncate">{r.title || "—"}</div>
                      <div className="mt-0.5">
                        €{(r.price ?? 0).toLocaleString()}
                        {" · "}{r.price_per_sqm != null ? `${r.price_per_sqm.toLocaleString()} €/m²` : "—"}
                      </div>
                      <div>
                        Δ {deltaValue}
                        {" · "}{t("mieten.mapScore", { score: r.deal_score ?? "—" })}
                      </div>
                    </div>
                  </LTooltip>
                </CircleMarker>
              );
            })}
          </MapContainer>
          {rows.length - mapPoints.length > 0 && (
            <div
              data-testid="mieten-map-no-coords"
              className="absolute bottom-3 left-3 z-[400] text-[10px] font-mono text-text-muted border border-border bg-bg/90 backdrop-blur px-2 py-1"
            >
              {t("mieten.mapNoCoords", { count: rows.length - mapPoints.length })}
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-px bg-border border-b border-border" data-testid="mieten-grid">
          {rows.map((r) => (
            <RentalCard key={r.unit_key} r={r} t={t} highlight={focusHighlight === r.unit_key} />
          ))}
          {rows.length === 0 && all.length === 0 && (
            <div className="col-span-3 p-10 text-center text-text-muted font-mono text-xs">
              {t("mieten.noRealData")}
            </div>
          )}
          {rows.length === 0 && all.length > 0 && (
            <div className="col-span-3 p-10 text-center text-text-muted font-mono text-xs">
              {t("mieten.noMatch")}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function RentalCard({ r, t, highlight }) {
  const deltaAccent = r.delta_pct == null ? "white" : r.delta_pct < 0 ? "green" : r.delta_pct > 0 ? "red" : "white";
  const deltaValue = r.delta_pct != null ? `${r.delta_pct > 0 ? "+" : ""}${r.delta_pct}%` : "—";

  const scoreAccent =
    r.deal_score == null ? "" :
    r.deal_score >= 70 ? "text-signal-green border-signal-green/50" :
    r.deal_score >= 40 ? "text-signal-yellow border-signal-yellow/50" :
    "text-text-muted border-border";
  const parts = r.deal_score_parts;
  const scoreTitle = parts
    ? t("mieten.scoreTooltip", { price: parts.price ?? "—", legal: parts.legal ?? "—", fresh: parts.fresh ?? "—" })
    : undefined;

  return (
    <div
      data-testid={`rental-card-${r.unit_key}`}
      className={`bg-bg hover:bg-surfaceHover text-left p-5 transition-colors group relative ${
        highlight ? "ring-1 ring-signal-green" : ""
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          {r.deal_score != null && (
            <span
              data-testid={`rental-card-score-${r.unit_key}`}
              title={scoreTitle}
              className={`text-[9px] font-mono border px-1.5 py-0.5 tracking-widest2 ${scoreAccent} ${
                r.segment === "gefoerdert" ? "opacity-60" : ""
              }`}
            >
              {r.deal_score}
            </span>
          )}
          <span className="overline">{r.platform}</span>
          {r.segment === "gefoerdert" && (
            <span
              data-testid={`rental-card-segment-${r.unit_key}`}
              className="text-[9px] font-mono text-signal-yellow border border-signal-yellow/50 px-1.5 py-0.5 tracking-widest2"
            >
              {t("mieten.badges.segment")}
            </span>
          )}
          {r.source_count > 1 && (
            <span
              data-testid={`rental-card-sources-${r.unit_key}`}
              className="text-[9px] font-mono border border-border text-text-muted px-1.5 py-0.5 tracking-widest2"
            >
              {t("mieten.badges.sources", { count: r.source_count })}
            </span>
          )}
          {r.is_new && (
            <span
              data-testid={`rental-card-new-${r.unit_key}`}
              className="text-[9px] font-mono text-signal-green border border-signal-green/50 px-1.5 py-0.5 tracking-widest2"
            >
              {t("mieten.badges.new")}
            </span>
          )}
          {r.mrg_savings_mo > 0 && (
            <span
              data-testid={`rental-card-mrg-${r.unit_key}`}
              className="text-[9px] font-mono text-signal-red border border-signal-red/50 px-1.5 py-0.5 tracking-widest2"
            >
              {t("mieten.badges.mrgSavings", { v: r.mrg_savings_mo.toLocaleString() })}
            </span>
          )}
          {r.befristet === true && (
            <span
              data-testid={`rental-card-befristet-${r.unit_key}`}
              className="text-[9px] font-mono text-signal-yellow border border-signal-yellow/50 px-1.5 py-0.5 tracking-widest2"
            >
              {t("mieten.badges.befristet")}
              {r.lease_end ? ` · ${t("mieten.badges.until", { date: String(r.lease_end).slice(0, 10) })}` : ""}
            </span>
          )}
        </div>
        {r.url && (
          <a
            href={r.url}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            title={t("mieten.openSource")}
            data-testid={`rental-card-source-${r.unit_key}`}
            className="text-text-muted hover:text-white transition-colors"
          >
            <ExternalLink size={12} />
          </a>
        )}
      </div>
      <div className="mt-4">
        <div className="text-xs text-text-muted font-mono">{r.market_name} · {r.country}</div>
        <div className="mt-1 font-heading text-lg leading-tight">{r.title || "—"}</div>
        <div className="text-[11px] text-text-muted truncate mt-0.5">
          {[r.location, r.postcode].filter(Boolean).join(" ")}
        </div>
      </div>

      <div className="mt-5 grid grid-cols-6 gap-3 border-t border-border pt-4">
        <Metric label={t("mieten.rent")} value={`€${(r.price ?? 0).toLocaleString()}`} />
        <Metric label="€/m²" value={r.price_per_sqm != null ? r.price_per_sqm.toLocaleString() : "—"} />
        <Metric
          label={t("mieten.deltaMarket")}
          value={
            <span data-testid={`rental-card-delta-${r.unit_key}`}>{deltaValue}</span>
          }
          accent={deltaAccent}
        />
        {/* Baujahr with inline MRG badge — same pattern as the Kauf page:
            mrg_applies === true flags MRG-Vollanwendung (Richtwert-bound rent). */}
        <Metric
          label={t("mieten.baujahr")}
          value={
            r.baujahr != null ? (
              <span className="inline-flex items-center gap-1.5">
                {r.baujahr}
                {r.mrg_applies === true && (
                  <span
                    data-testid={`rental-card-mrg-badge-${r.unit_key}`}
                    title={t("mieten.mrgBadgeTooltip")}
                    className="text-[9px] font-mono text-signal-yellow border border-signal-yellow/50 px-1 py-px tracking-widest2"
                  >
                    {t("mieten.mrgBadge")}
                  </span>
                )}
              </span>
            ) : "—"
          }
        />
        <Metric
          label={t("mieten.size")}
          value={r.rooms != null ? `${r.size_m2} m² · ${r.rooms} Zi` : `${r.size_m2} m²`}
        />
        <Metric label={t("mieten.dom")} value={`${r.days_on_market ?? "—"} T`} />
      </div>

      {r.bk_mo != null && (
        <div
          data-testid={`rental-card-bk-${r.unit_key}`}
          className="mt-3 text-[10px] font-mono text-text-muted"
        >
          {t("mieten.bkLine", { bk: r.bk_mo.toLocaleString() })}
          {r.kalt_price != null ? t("mieten.bkKaltPart", { kalt: r.kalt_price.toLocaleString() }) : ""}
        </div>
      )}
    </div>
  );
}
